import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container } from 'reactstrap';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import {
    Block,
    Heading3,
    TextInput,
    TextDefault,
    TextTiny,
    ThemedButton,
    FlexDiv,
    palette,
} from '@awarego-ui/components';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderText from '../../components/header/SubHeaderText';
import SubHeaderComponentsList from '../../components/header/SubHeaderComponentsList';
import HtmlField from '../../components/inputs/htmlField';
import { useStores } from '../../hooks/use-stores';
import AwarePhishSubNav from './AwarePhishSubNav';

const FieldLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${palette.graphite.charcoal};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 12px 0 6px;
`;

const EditorWrap = styled.div`
    margin-top: 4px;

    .RichTextEditor__root {
        max-height: 420px;
        display: flex;
        flex-direction: column;
    }

    .RichTextEditor__editor {
        max-height: 340px;
        overflow-y: auto;
    }

    .DraftEditor-root {
        min-height: 160px;
    }
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        text-align: left;
        font-size: 10px;
        font-weight: 600;
        color: ${palette.graphite.charcoal};
        text-transform: uppercase;
        letter-spacing: 0.06em;
        padding: 8px 12px;
        border-bottom: 1px solid ${palette.graphite.silver};
    }

    td {
        padding: 12px;
        border-bottom: 1px solid ${palette.graphite.silver};
        vertical-align: middle;
    }

    tbody tr.clickable {
        cursor: pointer;
        &:hover {
            background: ${palette.graphite.cloud};
        }
    }

    .actions {
        white-space: nowrap;
        text-align: right;
    }
`;

const AiPanel = styled.div`
    margin: 16px 0 8px;
    padding: 14px 16px;
    border: 1px solid ${palette.graphite.silver};
    border-radius: 6px;
    background: ${palette.graphite.white};
`;

const BriefTextarea = styled.textarea`
    display: block;
    width: 100%;
    box-sizing: border-box;
    min-height: 72px;
    padding: 10px 12px;
    margin: 0;
    font-size: 14px;
    font-family: inherit;
    line-height: 1.45;
    color: ${palette.graphite.heavyMetal};
    background: ${palette.graphite.white};
    border: 1px solid ${palette.graphite.silver};
    border-radius: 4px;
    resize: vertical;

    &:focus {
        outline: none;
        border-color: ${palette.evergreen.aspargus};
    }
`;

const ExampleChip = styled.button`
    display: inline-block;
    margin: 0 8px 8px 0;
    padding: 6px 10px;
    font-size: 12px;
    line-height: 1.35;
    text-align: left;
    color: ${palette.graphite.charcoal};
    background: ${palette.graphite.cloud};
    border: 1px solid ${palette.graphite.silver};
    border-radius: 4px;
    cursor: pointer;

    &:hover {
        border-color: ${palette.evergreen.aspargus};
        color: ${palette.evergreen.aspargus};
    }
`;

const DEFAULT_HTML =
    '<p>Hello {{.FirstName}},</p><p><a href="{{.URL}}">Click here</a></p>';

const AI_EXAMPLES = [
    'IT helpdesk: password reset after several failed sign-in attempts — urgent, account locked.',
    'HR benefits enrollment: update banking details before the deadline via a secure form.',
    'Shared document from leadership: review attached board pack before tomorrow’s meeting.',
];

const emptyForm = {
    name: '',
    subject: '',
    textBody: '',
    htmlBody: DEFAULT_HTML,
};

function AwarePhishTemplates() {
    const { store, awarePhishStore, commonStore } = useStores();
    const { currentCompany } = store;
    const companyId = currentCompany && currentCompany.company_id;
    const {
        templates,
        loadingTemplates,
        savingAsset,
        generatingTemplate,
    } = awarePhishStore;

    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [showForm, setShowForm] = useState(false);
    const [editorKey, setEditorKey] = useState(0);
    const [showAi, setShowAi] = useState(false);
    const [aiBrief, setAiBrief] = useState('');
    const [aiAbout, setAiAbout] = useState('');
    const formRef = useRef(form);
    formRef.current = form;

    useEffect(() => {
        if (companyId) awarePhishStore.loadTemplates(companyId);
    }, [companyId]);

    // Stable binding for HtmlField (react-rte); remount via editorKey on open/edit.
    const htmlField = useMemo(
        () => ({
            id: 'aware-phish-html-body',
            label: 'HTML body',
            get value() {
                return formRef.current.htmlBody || '';
            },
            rules: '',
            error: '',
            onChange: (html) => {
                setForm((prev) => ({ ...prev, htmlBody: html }));
            },
        }),
        [editorKey]
    );
    const mrkdownField = useMemo(
        () => ({
            id: 'aware-phish-html-body-md',
            onChange: () => {},
        }),
        [editorKey]
    );

    const startCreate = () => {
        setEditingId(null);
        setForm(emptyForm);
        setEditorKey((k) => k + 1);
        setShowAi(false);
        setAiBrief('');
        setAiAbout('');
        setShowForm(true);
    };

    const startEdit = (t) => {
        if (t.isPlatform) return;
        setEditingId(t.id);
        setForm({
            name: t.name || '',
            subject: t.subject || '',
            textBody: t.textBody || '',
            htmlBody: t.htmlBody || DEFAULT_HTML,
        });
        setEditorKey((k) => k + 1);
        setShowAi(false);
        setAiBrief('');
        setAiAbout('');
        setShowForm(true);
    };

    const save = async () => {
        try {
            await awarePhishStore.saveTemplate(companyId, editingId, form);
            commonStore.success(
                editingId ? 'Template updated' : 'Template created'
            );
            setShowForm(false);
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Failed to save template'
            );
        }
    };

    const remove = async (t, e) => {
        if (e) e.stopPropagation();
        if (t.isPlatform) return;
        if (!window.confirm(`Delete template “${t.name}”?`)) return;
        try {
            await awarePhishStore.removeTemplate(companyId, t.id);
            commonStore.success('Template deleted');
        } catch (err) {
            commonStore.error(
                awarePhishStore.error || 'Failed to delete template'
            );
        }
    };

    const runAiGenerate = async () => {
        try {
            const draft = await awarePhishStore.generateTemplate(companyId, {
                brief: aiBrief,
                aboutCompany: aiAbout,
            });
            setForm((prev) => ({
                ...prev,
                name: prev.name || draft.name || '',
                subject: draft.subject || prev.subject,
                htmlBody: draft.htmlBody || prev.htmlBody,
            }));
            setEditorKey((k) => k + 1);
            commonStore.success('Draft generated — review before saving');
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Failed to generate template'
            );
        }
    };

    return (
        <Container className="manager-content">
            <Helmet>
                <title>AwarePHISH templates</title>
            </Helmet>
            <HeaderSection
                heading="Email templates"
                subHeadings={
                    <SubHeaderComponentsList>
                        <SubHeaderText
                            text={
                                'Company email templates for phishing simulations. Include {{.URL}} for the phishing link.'
                            }
                        />
                    </SubHeaderComponentsList>
                }
                buttons={
                    <ThemedButton
                        text="+ New template"
                        mode="primary"
                        onClick={startCreate}
                        $noWrap
                    />
                }
            />
            <AwarePhishSubNav active="templates" />

            {showForm ? (
                <Block padding="20" column style={{ marginBottom: 14 }}>
                    <Heading3>
                        {editingId ? 'Edit template' : 'New template'}
                    </Heading3>
                    <FieldLabel>Name</FieldLabel>
                    <TextInput
                        fullWidth
                        value={form.name}
                        onChange={(e) =>
                            setForm({ ...form, name: e.target.value })
                        }
                    />
                    <FieldLabel>Subject</FieldLabel>
                    <TextInput
                        fullWidth
                        value={form.subject}
                        onChange={(e) =>
                            setForm({ ...form, subject: e.target.value })
                        }
                    />

                    <FlexDiv
                        alignCenter
                        gap={12}
                        style={{ marginTop: 16, flexWrap: 'nowrap' }}
                    >
                        <ThemedButton
                            text={showAi ? 'Hide AI Generate' : 'AI Generate'}
                            mode="secondary"
                            onClick={() => setShowAi((v) => !v)}
                            $noWrap
                        />
                        <TextTiny lighter>
                            Draft subject &amp; HTML from a short brief (uses
                            company name + your email for domain context).
                        </TextTiny>
                    </FlexDiv>

                    {showAi ? (
                        <AiPanel>
                            <FieldLabel style={{ marginTop: 0 }}>
                                What is the email about?
                            </FieldLabel>
                            <BriefTextarea
                                value={aiBrief}
                                onChange={(e) => setAiBrief(e.target.value)}
                                placeholder="A few sentences describing the phishing scenario…"
                            />
                            <TextTiny lighter style={{ margin: '8px 0 4px' }}>
                                Examples (click to use):
                            </TextTiny>
                            <div>
                                {AI_EXAMPLES.map((ex) => (
                                    <ExampleChip
                                        key={ex}
                                        type="button"
                                        onClick={() => setAiBrief(ex)}
                                    >
                                        {ex}
                                    </ExampleChip>
                                ))}
                            </div>
                            <FieldLabel>About the company (optional)</FieldLabel>
                            <BriefTextarea
                                value={aiAbout}
                                onChange={(e) => setAiAbout(e.target.value)}
                                placeholder="Industry, tone, products — anything that helps the lure feel real"
                                style={{ minHeight: 56 }}
                            />
                            <FlexDiv gap={12} style={{ marginTop: 12 }}>
                                <ThemedButton
                                    text={
                                        generatingTemplate
                                            ? 'Generating…'
                                            : 'Generate draft'
                                    }
                                    mode="primary"
                                    onClick={runAiGenerate}
                                    disabled={
                                        generatingTemplate ||
                                        !aiBrief.trim()
                                    }
                                    $noWrap
                                />
                            </FlexDiv>
                        </AiPanel>
                    ) : null}

                    <FieldLabel>HTML body</FieldLabel>
                    <TextTiny lighter style={{ marginBottom: 6 }}>
                        Use {'{{.URL}}'}, {'{{.FirstName}}'}, {'{{.LastName}}'}{' '}
                        as placeholders.
                    </TextTiny>
                    <EditorWrap>
                        <HtmlField
                            key={editorKey}
                            field={htmlField}
                            mrkdownField={mrkdownField}
                            showLabel={false}
                        />
                    </EditorWrap>
                    <FlexDiv gap={12} style={{ marginTop: 16 }}>
                        <ThemedButton
                            text={savingAsset ? 'Saving…' : 'Save'}
                            mode="primary"
                            onClick={save}
                            disabled={savingAsset}
                            $noWrap
                        />
                        <ThemedButton
                            text="Cancel"
                            mode="secondary"
                            onClick={() => setShowForm(false)}
                            $noWrap
                        />
                    </FlexDiv>
                </Block>
            ) : null}

            <Block tablePadding column>
                {loadingTemplates && !(templates || []).length ? (
                    <TextDefault lighter>Loading…</TextDefault>
                ) : !(templates || []).length ? (
                    <TextDefault lighter>
                        No templates yet. Create one with + New template (or use
                        AI Generate).
                    </TextDefault>
                ) : (
                    <Table>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Subject</th>
                                <th className="actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(templates || []).map((t) => (
                                <tr
                                    key={t.id}
                                    className={
                                        t.isPlatform ? '' : 'clickable'
                                    }
                                    onClick={() => startEdit(t)}
                                >
                                    <td>
                                        <TextDefault bold>
                                            {t.name}
                                        </TextDefault>
                                        {t.isPlatform ? (
                                            <div>
                                                <TextTiny lighter>
                                                    AwareGO default
                                                </TextTiny>
                                            </div>
                                        ) : null}
                                    </td>
                                    <td>
                                        <TextDefault>{t.subject}</TextDefault>
                                    </td>
                                    <td className="actions">
                                        {t.isPlatform ? (
                                            <TextTiny lighter>Read-only</TextTiny>
                                        ) : (
                                            <FlexDiv
                                                gap={8}
                                                style={{
                                                    justifyContent: 'flex-end',
                                                    flexWrap: 'nowrap',
                                                }}
                                            >
                                                <ThemedButton
                                                    text="Edit"
                                                    mode="secondary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        startEdit(t);
                                                    }}
                                                    $noWrap
                                                />
                                                <ThemedButton
                                                    text="Delete"
                                                    mode="secondary"
                                                    onClick={(e) =>
                                                        remove(t, e)
                                                    }
                                                    $noWrap
                                                />
                                            </FlexDiv>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Block>
        </Container>
    );
}

export default observer(AwarePhishTemplates);
